import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { useAccessPermissions, useCurrentUserRoleNames, usePermission } from "./useAccessPermissions";
import { useEffectiveOrgRole } from "./useEffectiveOrgRole";
import { isCrossOrgAdminRole } from "./RequireEcrOpsRole";
import UnauthorizedAccess from "./UnauthorizedAccess";

// Replaces the old RequireRoles (state.auth.permissions, populated once at
// login) with real, always-fresh RBAC data: entry is granted to whoever is
// Owner, Approver, Leadership, or OperationsUser of *this* org (per
// GET /user/organizations), OR to anyone holding one of the deliberately
// cross-org roles (EnterpriseAdmin, OperationsAdmin) whose permissions
// include organization:read. Leadership and OperationsUser are org-scoped
// like Owner/Approver, NOT part of the global override — see
// useEffectiveOrgRole's isLeadership/isOperationsUser and isCrossOrgAdminRole.
const RequireOrgRole = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);

  const { isLoading: isLoadingPermissions } = useAccessPermissions();
  const hasGlobalViewAccess = usePermission("organization:read");
  const roleNames = useCurrentUserRoleNames();
  const {
    isOwner,
    isApprover,
    isLeadership,
    isOperationsUser,
    isLoading: isLoadingOrgRole,
  } = useEffectiveOrgRole(orgId);

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoadingPermissions || isLoadingOrgRole) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const isAllowed =
    isOwner ||
    isApprover ||
    isLeadership ||
    isOperationsUser ||
    (hasGlobalViewAccess && isCrossOrgAdminRole(roleNames));

  if (!isAllowed) {
    return (
      <UnauthorizedAccess message="You are not authorized to access this organization's admin pages." />
    );
  }

  return <Outlet />;
};

export default RequireOrgRole;
